import Link from 'next/link';
import { GripVertical, Plus } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { WorkspaceBadge } from './WorkspaceBadge';
import { IconButton } from './ui';
import { useWorkspaceDrag, type WorkspaceDragModel } from './sidebar/hooks/use-workspace-drag';
import { useWorkspaceRows, type WorkspaceRowModel, type WorkspaceRowsModel } from './sidebar/hooks/use-workspace-rows';
// `dragCss`, not `drag`: the css-module guard scans for `<alias>.<class>` and the alias
// `drag` also matches `drag.module` inside this very path.
import dragCss from './sidebar/workspace-drag.module.css';

// The sidebar's Workspaces section. Each of the caller's top-level workspaces (from /api/workspaces →
// symphony-bff) is a FLAT row (badge + name): tapping it navigates straight into the workspace on its
// default tab — Growth & Goals (/workspace/[slug]) when it has a dashboard, else "Agents at work"
// (/workspace/[slug]/agents). No accordion, no caret, no sub-item rows: both destinations are reachable
// via the in-screen tab bar (WorkspaceTabBar), so the drawer stays a flat list (parity with the native
// drawer). The section header carries the create-a-space "+" (SpacesHeader); with no workspaces the
// section additionally shows a "Create a space" invitation row, so the feature is discoverable rather
// than silently absent. In the collapsed icon rail the text morphs
// away with the rail width (the .orion-rail-* classes), leaving the badges.

// Which workspaces there are, where each row goes and what a click reports lives in
// useWorkspaceRows — the mobile drawer renders the same rows with its own markup
// (components/symphony/sidebar/mobile/MobileWorkspaces.tsx).
export function WorkspaceNav({ collapsed = false }: { collapsed?: boolean }) {
  const { ready, rows, create } = useWorkspaceRows();
  // Reordering happens in place, by dragging a row's grip — the same behaviour the mobile drawer gets.
  const dnd = useWorkspaceDrag(rows);

  // While loading/errored we still render the section header so the divider and spacing
  // below don't collapse — only the workspace rows are omitted.
  if (!ready) {
    return (
      <div style={{ flexShrink: 0, padding: '4px 0' }}>
        <SpacesHeader create={create} />
      </div>
    );
  }
  return (
    <div style={{ flexShrink: 0, padding: '4px 0 16px' }}>
      {collapsed && (
        <div
          aria-hidden
          style={{
            height: 1,
            width: 28,
            marginInlineStart: 4,
            // Figma `Sidebar-Menu` (11525:177953): the 1px hairline sits in a 32px
            // band — 16px of breathing room above (on top of the 16px section gap)
            // and 28px below, down to the workspace rows.
            marginBlock: '16px 28px',
            background: 'var(--ds-separator-default)',
          }}
        />
      )}
      <SpacesHeader create={create} />
      {rows.length === 0 ? (
        <CreateWorkspaceRow create={create} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: collapsed ? 4 : 8 }}>
          {dnd.rows.map((row) => (
            <WorkspaceRow key={row.ws.slug} row={row} collapsed={collapsed} dnd={dnd} />
          ))}
        </div>
      )}
    </div>
  );
}

// The "Spaces" section header: the label, with the create-a-space "+" opposite it.
// The + is the SAME lucide glyph the Chat row carries (NavRail, `Plus size={16}`), and it
// fires the SAME ask as the home strip's "Create a space" (WorkspacesPanel) — a Maestro
// handoff, because spaces are agent-created and there is no create form to open. Parity:
// the narrow drawer (sidebar/mobile/MobileWorkspaces) and both native drawers
// (DrawerOverlay.workspacesSectionHeader) already carry this "+"; the wide rail was the
// one surface missing it.
//
// The label keeps its OWN block padding rather than the row carrying it, so the row's
// height and the label's position are exactly what they were before the + arrived (the
// 26px button centres inside the 36px row). `orion-rail-expanded-only` stays on the ROW,
// which is what the collapsed rail zeroes — a padded child inside it is clipped by the
// same `max-height: 0`, so the header still folds to nothing in the icon strip.
function SpacesHeader({ create }: { create: WorkspaceRowsModel['create'] }) {
  const { t } = useTranslation();
  return (
    <div
      className="orion-rail-expanded-only"
      style={{ display: 'flex', alignItems: 'center', paddingInlineEnd: 3 }}
    >
      <span
        style={{
          flex: 1,
          paddingInlineStart: 8,
          paddingBlock: '7px 12px',
          fontSize: 14,
          fontWeight: 400,
          letterSpacing: '-0.01em',
          color: 'var(--ds-text-secondary)',
        }}
      >
        {t('sidebar.workspaces')}
      </span>
      <IconButton
        href={create.route}
        linkAs={create.path}
        onClick={create.onClick}
        aria-label={create.label}
        size="tiny"
      >
        <Plus size={16} aria-hidden />
      </IconButton>
    </div>
  );
}

// The empty state is an invitation, not a dead line: a dashed tile + "Create a
// workspace" in the workspace-row geometry (the tile holds the icon column in
// the collapsed rail too), opening as a Maestro conversation — workspaces are
// agent-created, so the ask IS the create flow.
function CreateWorkspaceRow({ create }: { create: WorkspaceRowsModel['create'] }) {
  return (
    <Link
      href={create.route}
      as={create.path}
      onClick={create.onClick}
      title={create.label}
      className="orion-rail-expanded-only"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        margin: '0 4px',
        paddingBlock: 9,
        paddingInlineEnd: 8,
        paddingInlineStart: 4,
        borderRadius: 10,
        textDecoration: 'none',
        color: 'rgba(var(--ink-rgb),0.6)',
        fontSize: 14,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: 9,
          border: '1.5px dashed rgba(var(--ink-rgb),0.25)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'rgba(var(--ink-rgb),0.5)',
        }}
      >
        <Plus size={12} aria-hidden />
      </span>
      <span className="orion-rail-label">{create.label}</span>
    </Link>
  );
}

// A FLAT workspace row: the badge + name, navigating straight into the workspace on its default
// tab — Growth & Goals (the dashboard) when the home index says it has one, else Agents at work
// (an empty Growth & Goals would be a dead landing). No caret, no expand/collapse, no sub-item
// rows — the in-screen WorkspaceTabBar owns tab switching (parity with the native drawer).
function WorkspaceRow({ row, collapsed = false, dnd }: { row: WorkspaceRowModel; collapsed?: boolean; dnd: WorkspaceDragModel }) {
  const { t } = useTranslation();
  const { ws, active } = row;
  const dragging = dnd.isDragging(ws.id);
  return (
    <Link
      href={row.href}
      as={row.as}
      onClick={row.onClick}
      {...dnd.rowProps()}
      aria-current={active ? 'page' : undefined}
      title={ws.name}
      className={`orion-nav-item ${dragCss.row} ${dragging ? dragCss.lifted : ''}`}
      style={{
        ...dnd.liftStyle(ws.id),
        ...workspaceRowStyle,
        boxSizing: 'border-box',
        width: collapsed ? 36 : '100%',
        paddingBlock: collapsed ? 2 : 8,
        paddingInline: collapsed ? 2 : 8,
        justifyContent: collapsed ? 'center' : undefined,
        // The label is clipped to zero width when collapsed, but a gap before it is
        // still counted as content — so centring pushed the badge left, off the icon
        // column and past its own row's edge. Same reason NavItem zeroes its gap.
        gap: collapsed ? 0 : 12,
      }}
    >
      {/* No grip when collapsed (no room, and the label is clipped) or when this is the only space. */}
      {!collapsed && dnd.canReorder && (
        <span
          className={dragCss.grip}
          role="button"
          tabIndex={0}
          aria-label={t('workspace.reorder')}
          {...dnd.gripProps(ws.id)}
        >
          <GripVertical size={14} aria-hidden />
        </span>
      )}
      <WorkspaceBadge ws={ws} size={20} />
      <span
        className="orion-rail-label"
        style={{
          flex: 1,
          textAlign: 'start',
          fontSize: 14,
          fontWeight: active ? 700 : 600,
          color: 'var(--ds-text-primary)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {ws.name}
      </span>
    </Link>
  );
}

const workspaceRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  margin: 0,
  paddingBlock: 6,
  paddingInlineEnd: 8,
  paddingInlineStart: 8,
  borderRadius: 8,
  textDecoration: 'none',
  color: 'var(--ds-text-primary)',
  fontFamily: 'inherit',
  transition: 'background-color 0.15s ease',
};
